import { Link } from "react-router-dom";
import "./WorkTimeAccountDashboard.css";
import BackButton from "../../../../../sharedComponents/backButton/BackButton";
import { CustomProvider, DatePicker } from "rsuite";
import de_DE from 'rsuite/locales/de_DE';
import { useEffect, useState } from "react";
import axios from "axios";
import LoadingSpinner from "../../../../../sharedComponents/LoadingSpinner/LoadingSpinner";
import { DataGrid } from '@mui/x-data-grid';
import formatDuration from "../../../../../sharedComponents/FormatDuration";
import parseDuration from "../../../../../sharedComponents/ParseDuration";
import PrimaryButton from "../../../../../sharedComponents/primaryButton/PrimaryButton";
import dayjs from "dayjs";
import FilterArea from "../../../../../sharedComponents/filterArea/FilterArea";

const columns = [
    { field: 'firstName', headerName: 'Vorname', width: 100 },
    { field: 'lastName', headerName: 'Nachname', width: 150 },
    { field: 'transfer', headerName: "Transfer (Bilanz) aus dem letzten Monat", width: 100 },
    { field: 'soll-time', headerName: "Soll-Arbeitszeit", width: 150 },
    { field: 'ist-time', headerName: "Aufgezeichnete Ist-Arbeitszeit", width: 250 },
    { field: 'absences', headerName: "Summe Urlaub mit LFZ", width: 200 },
    { field: 'illness', headerName: "Summe Krankheit mit LFZ", width: 200 },
    { field: 'holidays', headerName: "Summe Feiertage mit LFZ", width: 200 },
    { field: 'furtherEducation', headerName: "Summe Weiterbildung mit LFZ", width: 200 },
    { field: 'vocationalSchool', headerName: "Summe Berufsschule mit LFZ", width: 200 },
    { field: 'university', headerName: "Summe Universität mit LFZ", width: 200 },
    { field: 'school', headerName: "Summe Schule mit LFZ", width: 200 },
    { field: 'other', headerName: "Summe Sonstige mit LFZ", width: 200 },
    { field: 'correction', headerName: "Summe Ist-Zeit Korrekturen", width: 230 },
    { field: 'bilance', headerName: "Bilanz", width: 100 },
];

function WorkTimeAccountDashboard() {

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date(), 'MMM yyyy');
    const [workingTimeAccounts, setWorkingTimeAccounts] = useState(null);

    const [selectedArea, setSelectedArea] = useState(null);

    useEffect(() => {
        getExistingWorkingTimeAccounts(formatInYearMonth(selectedMonth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setIsLoading(true);
        getExistingWorkingTimeAccounts(formatInYearMonth(selectedMonth));
    }, [selectedMonth])

    function formatInYearMonth(date) {
        let currentYear = date.getFullYear();
        let currentMonth = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${currentYear}-${currentMonth}`;
    }

    const handleExportPDFDownload = () => {
        axios.get('workingTimeAccount/export/fullMonthly/pdf?yearmonth='+ formatInYearMonth(selectedMonth),
        {
            responseType: 'blob',
            headers: {'Content-Type': 'application/json'},
        }
        ).then((results) => 
            createPDF(results, 'Gesamt Arbeitszeitkonto Export ' + selectedMonth.toLocaleDateString('de', {month: 'long', year: 'numeric'}))
        )
    }

    function createPDF(results, filename){
        const data = results.data
        const blob = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileURL;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    function getExistingWorkingTimeAccounts(yearmonth) {
        setHasError(false);
        axios.get(`workingTimeAccount/getAllWhichExistInGivenMonth?yearmonth=${yearmonth}`).then(results => {
            const workingTimeAccountsData = results.data || [];
            axios.get('employee/getActive').then(resultsData => {
                (resultsData.data || []).forEach(employee => {
                    const matchingAccountIndex = workingTimeAccountsData.findIndex(account => account.employee_id === employee.id);
                    if (matchingAccountIndex !== -1) {
                        workingTimeAccountsData[matchingAccountIndex].first_name = employee.first_name;
                        workingTimeAccountsData[matchingAccountIndex].last_name = employee.last_name;
                    }
                })

                setWorkingTimeAccounts(workingTimeAccountsData);
                setIsLoading(false);
            }).catch(() => {
                setHasError(true);
                setIsLoading(false);
            })
        }).catch(() => {
            setHasError(true);
            setIsLoading(false);
        })
    }

    const rowClassGetter = (params) => {
        const rowData = params.row;
        const fullRowData = workingTimeAccounts.find(element => element.id === rowData.id);
        if ((fullRowData.error_not_completed_shift_begin_ids.length !== 0) || (fullRowData.error_pause_overflow_shift_begin_ids.length !== 0) || (fullRowData.error_unresolved_shift_objection_shift_begin_ids.length !== 0)) {
            return 'grid-row-red-color';
        } else if ((fullRowData.warning_possible_holiday_collision_shift_begin_ids.length !== 0)) {
            return 'grid-row-yellow-color';
        }
    }

    const generateRows = () => {
        let rows = [];

        workingTimeAccounts.forEach(workTimeAccount => {
            if(selectedArea !== null && !selectedArea.assigned_employee_ids.includes(workTimeAccount.employee_id)){
                return null;
            }

            let currentMonth = workTimeAccount.months.filter(workingTimeMonth => workingTimeMonth.month === formatInYearMonth(selectedMonth))[0];
            if (currentMonth !== undefined) {
                let row = {
                    id: workTimeAccount.id,
                    firstName: workTimeAccount.employee_first_name,
                    lastName: workTimeAccount.employee_last_name,
                    transfer: formatDuration(parseDuration(currentMonth.transfer_from_last_month)),
                    'soll-time': formatDuration(parseDuration(currentMonth.target_working_hours)),
                    'ist-time': formatDuration(parseDuration(currentMonth.total_tracked_shifts_duration)),
                    absences: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.vacation)),
                    illness: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.illness)),
                    holidays: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.holiday)),
                    furtherEducation: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.further_education)),
                    vocationalSchool: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.vocational_school)),
                    university: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.university)),
                    school: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.school)),
                    other: formatDuration(parseDuration(currentMonth.total_payed_absence_duration_by_type?.other)),
                    correction: formatDuration(parseDuration(currentMonth.total_sum_of_corrections)),
                    bilance: formatDuration(parseDuration(currentMonth.result)),
                }
                rows.push(row);
            }
        })

        return rows;
    }

    function generateDataTable() {
        return isLoading ?
            <LoadingSpinner />
            :
            hasError ?
            <p>Ein Fehler ist aufgetreten</p>
            :
            <DataGrid
                rows={generateRows()}
                columns={columns}
                disableRowSelectionOnClick
                hideFooterPagination
                density="compact"
                disableColumnMenu
                hideFooter
                getRowClassName={rowClassGetter}
            />
    }

    const isFutureMonth = selectedMonth && dayjs(selectedMonth).isAfter(dayjs(), "month");

    return (
        <>
            <Link to={'/home/dashboard'}>
                <div className="employee-information-header-container">
                    <BackButton text="Zurück" />
                </div>
            </Link>
            <div className="work-time-account-dasboard">
                <div className="work-time-account-dashboard-header">
                    <CustomProvider locale={de_DE}>
                        <DatePicker className="dashboard-datepicker" cleanable={false} label="Ausgewählter Monat" oneTap required format="MMM yyyy" size="lg" placeholder="Startmonat" value={selectedMonth} onSelect={(value) => setSelectedMonth(value)}/>
                    </CustomProvider>
                    <FilterArea selectedArea={selectedArea} setSelectedArea={setSelectedArea} />
                </div>
                {isFutureMonth && <p className="red-color">Achtung: Monat liegt in der Zukunft. Werte können nicht genau berechnet werden.</p>}
                {generateDataTable()}
                <PrimaryButton onClick={handleExportPDFDownload} text={'Download Export'} />
            </div>            
        </>
    )
}

export default WorkTimeAccountDashboard;